import React, { useState } from "react";
import { IoGridOutline } from "react-icons/io5";
import { BsFilter } from "react-icons/bs";
import { PiListLight } from "react-icons/pi";
import { AiOutlineSearch } from "react-icons/ai";
import { SlRefresh } from "react-icons/sl";
import { useNavigate, useLocation } from "react-router-dom";
import { useEntitlements } from "../../components/DataContext/EntitlementsContext";

interface MenuBarProps {
  onFilterToggle: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onRefresh: () => void;
}

const MenuBar: React.FC<MenuBarProps> = ({
  onFilterToggle,
  searchQuery,
  setSearchQuery,
  onRefresh,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchEntitlements } = useEntitlements();

  const handleSelect = (type: string) => {
    navigate(type === "grid" ? "/landing" : "/lineview");
  };

  const handleRefresh = async () => {
    try {
      onRefresh();
      await refetchEntitlements();
    } catch (error) {
      console.error("Refresh failed:", error);
    }
  };

  const isGridSelected = location.pathname === "/landing";
  const isListSelected = location.pathname === "/lineview";

  return (
    <div className="flex justify-between items-center pt-2 pb-4 bg-[#F7F7FF] mt-5">
      <div className="flex items-center ml-auto mr-4 sm:mr-5 md:mr-10 lg:mr-20 xl:mr-30 3xl:pr-40 gap-3 max-w-screen-2xl">
        {/* Search Box */}
        <div
          className={`relative flex items-center transition-all duration-300 ${
            isFocused ? "w-full sm:w-80 md:w-96" : "w-full sm:w-52 md:w-60"
          } bg-white rounded-full border-2 px-4 py-2 overflow-hidden`}
        >
          <AiOutlineSearch
            className={`w-5 h-5 text-gray-500 transition-all duration-300 transform ${
              isFocused ? "scale-125" : "scale-100"
            }`}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Search..."
            className="w-full bg-transparent text-gray-700 outline-none pl-3"
          />
        </div>

        {/* Refresh Button */}
        <button
          className="text-gray-400 hover:text-[#FF3B30]"
          onClick={handleRefresh}
        >
          <SlRefresh className="w-6 h-6" />
        </button>

        {/* View and Filter Controls */}
        <div className="flex gap-3 items-center">
          <button
            className={`flex items-center gap-2 ${
              isGridSelected ? "text-[#FF3B30]" : "text-gray-400"
            }`}
            onClick={() => handleSelect("grid")}
          >
            <IoGridOutline className="w-6 h-6" />
          </button>

          <button
            className={`flex items-center gap-2 ${
              isListSelected ? "text-[#FF3B30]" : "text-gray-400"
            }`}
            onClick={() => handleSelect("list")}
          >
            <PiListLight className="w-6 h-6" />
          </button>

          <button
            className="flex items-center gap-2 text-gray-400 hover:text-[#FF3B30]"
            onClick={onFilterToggle}
          >
            <BsFilter className="w-6 h-6" />
            <span className="text-sm">Filters</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
